import _ from 'lodash';
import Vue from 'vue';
import Axios, {CancelTokenSource} from 'axios';
import {mapActions, mapGetters, mapState} from 'vuex';
import EventHub from '../../lib/eventHub';
import {AlertNotificationModuleConstant, ClubLocationsModuleConstant, EmployeeProfileModuleConstant, MemberSearchModuleConstant, PageDriverConstant, RegularExpressionConstant, SectionConstant, UrlModuleConstant} from '../../lib/constants';
import {clubService} from '../../lib/services/clubService';
import {coachService} from '../../lib/services/coachService';
import {memberSearchService} from '../../lib/services/memberSearchService';
import MemberSearchPartial from './memberSearch.html';
import EcCommon from '../../../types/ec/ecCommon';
import EcMemberSearch from '../../../types/ec/ecMemberSearch';

interface IComponentData {
	clubService: EcCommon.IClubService;
	coachService: EcCommon.ICoachService;
	initializing: boolean;
	memberSearchService: EcMemberSearch.IMemberSearchService;
	searchTerm: string;
}

let searchMemberTokenSource = {} as CancelTokenSource;
let searchMemberPhotoTokenSource = {} as CancelTokenSource;

export default Vue.component('ecMemberSearch', {
	template: MemberSearchPartial,
	data(): IComponentData {
		return {
			clubService,
			coachService,
			initializing: true,
			memberSearchService,
			searchTerm: '',
		};
	},
	computed: {
		clubIds(): number[] {
			if (this.selectedClubs.length > 0) {
				return _.map(this.selectedClubs, 'ClubId');
			} else {
				return _.map(this.getClubs, 'ClubId');
			}
		},
		errorMessage(): string {
			let errorMessage = '';
			if (this.coachService.errorMessage) {
				errorMessage = this.coachService.errorMessage;
			}

			if (this.memberSearchService.errorMessage) {
				errorMessage = this.memberSearchService.errorMessage;
			}

			return errorMessage;
		},
		loading(): boolean {
			return this.initializing || this.clubService.loading || this.coachService.loading;
		},
		searching(): boolean {
			return this.memberSearchService.loading;
		},
		...mapGetters(ClubLocationsModuleConstant.name, [
			ClubLocationsModuleConstant.getter.getClubObjects,
			ClubLocationsModuleConstant.getter.getClubs
		]),
		...mapGetters(EmployeeProfileModuleConstant.name, {
			getIsEmployeeProfileValid: EmployeeProfileModuleConstant.getter.getIsValid
		}),
		...mapGetters(UrlModuleConstant.name, [UrlModuleConstant.getter.getGlobalPageDriverUrl]),
		...mapState(ClubLocationsModuleConstant.name, [ClubLocationsModuleConstant.state.selectedClubs]),
		...mapState(MemberSearchModuleConstant.name, [MemberSearchModuleConstant.state.members]),
	},
	methods: {
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
		}),
		...mapActions(ClubLocationsModuleConstant.name, [
			ClubLocationsModuleConstant.action.getClubLocations,
			ClubLocationsModuleConstant.action.selectClub
		]),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(MemberSearchModuleConstant.name, [
			MemberSearchModuleConstant.action.searchMembers,
			MemberSearchModuleConstant.action.clearMembers,
			MemberSearchModuleConstant.action.searchMemberPhoto,
			MemberSearchModuleConstant.action.searchConnectedStatus
		]),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		search(): Promise<void> | void {
			const searchMemberCancelToken = Axios.CancelToken;
			const searchMemberPhotoCancelToken = Axios.CancelToken;

			if (searchMemberTokenSource && searchMemberTokenSource.cancel) {
				searchMemberTokenSource.cancel('Request Canceled');
			}

			if (searchMemberPhotoTokenSource && searchMemberPhotoTokenSource.cancel) {
				searchMemberPhotoTokenSource.cancel('Request Canceled - Ignore Loading');
			}

			if (this.searchTerm && this.searchTerm.length >= 3) {
				searchMemberTokenSource = searchMemberCancelToken.source();
				const searchMemberObj: EcMemberSearch.ISearchRequestObject = {
					clubIds: this.clubIds,
					term: this.searchTerm,
					token: searchMemberTokenSource.token,
				};

				return this.clearMembers().then(() => {
					return this.searchMembers(searchMemberObj).then(() => {
						const memberIds = _.map(this.members, (member) => {
							return member.memberId;
						});
						if (memberIds.length > 0) {
							searchMemberPhotoTokenSource = searchMemberPhotoCancelToken.source();
							const searchMemberPhotoObj: EcMemberSearch.ISearchMemberPhotoRequestObject = {
								memberIds,
								token: searchMemberPhotoTokenSource.token,
							};

							return Promise.all([this.searchConnectedStatus(), this.searchMemberPhoto(searchMemberPhotoObj)])
								.then(() => {
									// TBD
								});
						}
					});
				});
			} else {
				this.clearMembers();
			}
		},
		clearTerm(): void {
			this.searchTerm = '';
			this.clearMembers().then(() => {
				if (searchMemberTokenSource && searchMemberTokenSource.cancel) {
					searchMemberTokenSource.cancel('Request Canceled');
				}

				if (searchMemberPhotoTokenSource && searchMemberPhotoTokenSource.cancel) {
					searchMemberPhotoTokenSource.cancel('Request Canceled - Ignore Loading');
				}

				this.$nextTick(() => {
					const el = this.$refs.searchText as HTMLElement;
					el.click();
					el.focus();
				});
			});
		},
	},
	created(): void {
		EventHub.$on('membersearch-clubLocations-optionStateChanged', (data: EcCommon.IMultiSelectGroupOptions) => {
			this.selectClub(data);
		});

		if (window.__PRERENDER_INJECTED || RegularExpressionConstant.authorRegex.test(window.location.href)) {
			return;
		}

		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore()]).then(() => {
			if (!this.getIsEmployeeProfileValid) {
				window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.loginResource));
			}

			this.getClubLocations();
		}).finally(() => {
			this.initializing = false;
		});
	},
	watch: {
		selectedClubs: 'search',
	},
});
